import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Tree, Tooltip, Typography, Icons } from '~/components';
import { fileManagerPaiDownload } from '~/services/FileManager/file-manager.service';
import { TreeTransparente } from './styles';
import RemoveDiacritic from '../../../hooks/removeDiacritic';
import { bindAntTreeInputA11y } from '~/modules/Helper/antdTreeA11y.helper';
import {
  EXPANSAO_ARVORE,
  montarChavesExpansao,
} from '../helpers/expansaoArvore.helper';

const TreeViewModuloFixo = forwardRef(
  ({ treeView, visao, setSearchValue, expansaoArvore = EXPANSAO_ARVORE.FECHADO }, ref) => {
    const { TreeNode } = Tree;
    const { corTema } = useSelector(state => state.configuration);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [autoExpandParent, setAutoExpandParent] = useState(true);
    const [treeData, setTreeData] = useState(treeView);
    const treeWrapRef = useRef(null);

    useEffect(() => {
      setSearchValue('');
      setTreeData(treeView);
      setExpandedKeys(montarChavesExpansao(treeView, expansaoArvore));
      setAutoExpandParent(Number(expansaoArvore) !== EXPANSAO_ARVORE.FECHADO);
    }, [expansaoArvore, treeView]);

    useEffect(
      () =>
        bindAntTreeInputA11y(
          treeWrapRef.current,
          'Navegação da árvore do módulo'
        ),
      [treeData]
    );
    function renderDetalhes(item) {
      return (
        <Tooltip title="Detalhes" placement="top">
          <Link
            to={`${visao}/${item.key}`}
            style={{ display: 'flex', cursor: 'pointer' }}
            aria-label={`Detalhes de ${item.title}`}
            onClick={event => event.stopPropagation()}
          >
            <Icons.InfoCircleOutlined
              style={{ fontSize: '18px', color: corTema }}
              aria-hidden
            />
          </Link>
        </Tooltip>
      );
    }

    function renderDownload(titulo, url) {
      if (!url) return null;

      return (
        <Tooltip title="Baixar arquivo" placement="top">
          <span
            role="button"
            tabIndex={0}
            aria-label={`Baixar arquivo de ${titulo}`}
            onClick={event => {
              event.stopPropagation();
              handleActivate(event, () => fileManagerPaiDownload(url));
            }}
            onKeyDown={event =>
              handleActivate(event, () => fileManagerPaiDownload(url))
            }
            style={{ display: 'inline-flex', cursor: 'pointer' }}
          >
            <Icons.DownloadOutlined
              style={{ fontSize: '18px', color: corTema }}
              aria-hidden
            />
          </span>
        </Tooltip>
      );
    }

    function handleActivate(event, action) {
      if (
        event.type === 'click' ||
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.keyCode === 13
      ) {
        if (event.type === 'keydown') event.preventDefault();
        action();
      }
    }

    const loop = data =>
      data.map(item => {
        if (
          (item.children && item.children.length) ||
          item.PrimeiroNivel
        ) {
          return (
            <TreeNode
              key={item.key}
              title={
                item.PrimeiroNivel ? (
                  <Typography.Text
                    style={{
                      display: 'block',
                      width: '100%',
                      outline: 0,
                      cursor: 'default',
                      marginRight: -15,
                    }}
                  >
                    {item.title}
                  </Typography.Text>
                ) : (
                  <Typography.Text
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      gap: '0.5rem',
                      outline: 0,
                      cursor: 'default',
                    }}
                  >
                    <span>{item.title}</span>
                    {item.ExibeDetalhes && (
                      <Tooltip title="Informações" placement="top">
                        <Link
                          to={`${visao}/${item.key}`}
                          style={{ display: 'flex', cursor: 'pointer' }}
                          aria-label={`Informações sobre ${item.title}`}
                          onClick={event => event.stopPropagation()}
                        >
                          <Icons.InfoCircleOutlined
                            style={{ fontSize: '18px', color: corTema }}
                            aria-hidden
                          />
                        </Link>
                      </Tooltip>
                    )}
                    {renderDownload(item.title, item.Arquivo?.Url)}
                  </Typography.Text>
                )
              }
            >
              {loop(item.children)}
            </TreeNode>
          );
        }
        if (item.Externo) {
          return (
            <TreeNode
              key={item.key}
              isLeaf
              title={
                <Typography.Text
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: '0.5rem',
                    outline: 0,
                    cursor: 'default',
                  }}
                >
                  <span>{item.title}</span>
                  {renderDownload(item.title, item.Link)}
                </Typography.Text>
              }
            />
          );
        }
        return (
          <TreeNode
            key={item.key}
            isLeaf
            title={
              <Typography.Text
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  gap: '0.5rem',
                  outline: 0,
                  cursor: 'default',
                }}
              >
                <span>{item.title}</span>
                {renderDetalhes(item)}
                {item.Arquivo?.Url
                  ? renderDownload(item.title, item.Arquivo.Url)
                  : null}
              </Typography.Text>
            }
          />
        );
      });

    const toggleExpandNode = key => {
      setExpandedKeys(prev => {
        const outArr = [...prev];
        if (outArr.includes(key)) {
          return outArr.filter(e => e !== key);
        }
        outArr.push(key);
        return outArr;
      });
      setAutoExpandParent(false);
    };

    const onExpand = keys => {
      setExpandedKeys(keys);
      setAutoExpandParent(false);
    };

    useImperativeHandle(ref, () => ({
      onSearch(busca) {
        const searchWithoutAccents = RemoveDiacritic(busca);
        if (searchWithoutAccents) {
          const hasMatchWord = string => {
            const stringWithoutAccents = RemoveDiacritic(string);
            return stringWithoutAccents?.indexOf(searchWithoutAccents) !== -1;
          };
          const filterData = arr =>
            arr?.filter(
              item =>
                hasMatchWord(item.title) || filterData(item.children)?.length
            );
          const filteredData = filterData(treeView).map(n => {
            return {
              ...n,
              children: filterData(n.children),
            };
          });

          const nextExpandedKeys = filteredData?.map(dataItem => dataItem.key);

          setTreeData(filteredData);
          setExpandedKeys(nextExpandedKeys);
          setAutoExpandParent(true);
        } else {
          setSearchValue('');
          setTreeData(treeView);
          setExpandedKeys(montarChavesExpansao(treeView, expansaoArvore));
          setAutoExpandParent(Number(expansaoArvore) !== EXPANSAO_ARVORE.FECHADO);
        }
      },
      onChange(e) {
        setSearchValue(e.target.value);
        if (!e.target.value) {
          setExpandedKeys(montarChavesExpansao(treeView, expansaoArvore));
          setTreeData(treeView);
        }
      },
    }));

    return (
      <div ref={treeWrapRef}>
        <TreeTransparente
          className="draggable-tree"
          draggable={false}
          defaultExpandAll={false}
          blockNode
          selectable
          height={600}
          onExpand={onExpand}
          expandedKeys={expandedKeys}
          autoExpandParent={autoExpandParent}
          titleRender={record => (
            <span
              key={record.key}
              onClick={() => toggleExpandNode(record.key)}
              style={{ display: 'inline-block', width: '100%', height: '100%' }}
            >
              {record.title}
            </span>
          )}
        >
          {loop(treeData)}
        </TreeTransparente>
      </div>
    );
  }
);

export default TreeViewModuloFixo;
